import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack, Divider } from "@mui/material";
import ItemInput from "../../components/Input/ItemInput";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import FileInput from "../../components/Input/FileInput";
import { ASSET_ISSUE_LIST } from "../../config/constants/common";
import { useDmsForm } from "../../hooks/useDmsFrom";
import appRoutePath from "../../config/path";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { assetApi } from "../../services/assets/asset-crud";
import { assetReportApi } from "../../services/asset-report/index";
import { formatDate } from "../../utils/common";
import { AssetLogsFilter } from "../../common-ui/page-filters/AssetLogsFilter";
import {
  IAssetRecord,
  IPagination,
  IQueryPerams,
  IAssetReport,
} from "../../config/types";
import { alertDialogStore, actionDialogStore } from "../../store/dialogs";
import useAppNavigation from "../../hooks/useAppNavigation";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import VerticalGap from "../../components/Gap/VerticalGap";
import { Chip } from "@mui/material";

interface UpdateAssetReportForm {
  issue: string;
  comments: string;
  resolution: string;
}

const defaultValue = <T,>(value: T) => ({ isValid: true, value });

const UpdateAssetReportForm = () => {
  const [reportData, setReportData] = useState<IAssetReport | null>(null);
  const [reportImages, setReportImages] = useState<File[] | null>(null);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([]);
  const [resetFiles, setResetFiles] = useState(false);
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const [originalAssetIds, setOriginalAssetIds] = useState<string[]>([]);

  const hasPreSelectedRef = useRef(false);

  const navigate = useAppNavigation();
  const target = useParams<{ id: string }>();

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
    addRows,
  } = useMangeTableItems(dataset);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetApi.getAssets);

  const {
    form,
    updateForm,
    updateInputField,
    fieldHasError,
    setSubmitAttempt,
    onFormSubmit,
  } = useDmsForm<UpdateAssetReportForm>({
    issue: "",
    comments: "",
    resolution: "",
  });

  useEffect(() => {
    const fetchReportDetail = async () => {
      if (!target?.id) return;

      const res = await assetReportApi.getAssetReport(target.id);
      if (res.data) {
        const data = res.data;
        setReportData(data);
        setExistingImages(data.evidence_images || []);

        const assetIds = data.assets?.map((asset: any) => asset.id) || [];
        setOriginalAssetIds(assetIds);

        updateForm({
          issue: defaultValue(data.issue),
          comments: defaultValue(data.comments),
          resolution: defaultValue(data.resolution),
        });
      }
    };
    fetchReportDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      brand: `${data.brand}(${data.model})`,
      date_received: formatDate(data.date_received),
      warranty_in_months: data.warranty_in_months ?? "Not Stated",
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `,
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchAssetsData = useCallback(async () => {
    const result = await assetApi.getAssets({ is_deleted: false });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAssetsData();
  }, [fetchAssetsData]);

  useEffect(() => {
    if (
      originalAssetIds.length > 0 &&
      dataset.length > 0 &&
      !hasPreSelectedRef.current
    ) {
      const preSelectedItems = dataset.filter((item) =>
        originalAssetIds.includes(item.id),
      );

      if (preSelectedItems.length > 0) {
        addRows(preSelectedItems);
        hasPreSelectedRef.current = true;
      }
    }
  }, [originalAssetIds, dataset, addRows]);

  const handleImageSelect = (files: File[] | File | null) => {
    const filesArray = Array.isArray(files) ? files : files ? [files] : null;
    setReportImages(filesArray);
  };

  const handleRemoveExistingImage = (imageId: string) => {
    setRemovedImageIds((prev) => [...prev, imageId]);
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleIssueSelect = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    updateForm((p) => ({
      ...p,
      issue: {
        value: value,
        isValid: value !== "",
        errorMessage: value === "" ? "Please select an issue" : "",
      },
    }));
  };

  function cancelUpdate() {
    alertDialogStore.open({
      title: "Unsaved Changes",
      subtitle: `Do you want to discard changes?`,
      onRightBtnPress: () =>
        navigate({ path: appRoutePath.ASSET_REPORT_HISTORY }),
      isSuccess: false,
    });
  }

  async function toggleUpdateBtn() {
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit();

    if (isValid) {
      const updateRequestFtn = async () => {
        const currentAssetIds = selectedRowItems.map((item) => item.id);

        const removed_asset_ids = originalAssetIds.filter(
          (id) => !currentAssetIds.includes(id),
        );

        const newly_added_asset_ids = currentAssetIds.filter(
          (id) => !originalAssetIds.includes(id),
        );

        const updateData: any = {
          issue: data.issue,
          comments: data.comments,
          resolution: data.resolution,
        };

        if (removedImageIds.length > 0) {
          updateData.removed_evidence_images = removedImageIds;
        }

        if (removed_asset_ids.length > 0) {
          updateData.removed_asset_ids = removed_asset_ids;
        }

        if (newly_added_asset_ids.length > 0) {
          updateData.newly_added_asset_ids = newly_added_asset_ids;
        }

        const response = await assetReportApi.updateAssetReport(
          target.id ?? "",
          updateData,
          reportImages || undefined,
        );

        alertDialogStore.open({
          title: "Update Asset Report",
          subtitle:
            response.message ||
            (response.success
              ? "Asset report updated successfully"
              : "Failed to update asset report"),
          rightBtnTxt: `${response.success ? "See All Reports" : "Try again"}`,
          onRightBtnPress: async () => {
            if (response.success) {
              navigate({ path: appRoutePath.ASSET_REPORT_HISTORY });
            }
          },
          isSuccess: response.success,
        });
      };

      actionDialogStore.open({
        title: "Update Asset Report",
        subtitle: 'Click "Confirm" to update asset report',
        onRightBtnPress: updateRequestFtn,
      });
    }
  }

  const handleResetComplete = () => {
    setResetFiles(false);
  };

  if (target.id && !reportData) {
    return null;
  }

  return (
    <MainPageBase
      title={"Update Asset Report"}
      pageState={"add-item"}
      cancelBtnClick={cancelUpdate}
      addItemBtnOnClick={toggleUpdateBtn}
    >
      <Box height="100%" display="flex" justifyContent="center">
        <Box width="100%" m="0px 32px">
          <Stack direction="row" gap={4}>
            <ExpandableContainer
              label="Asset Report Information"
              initOpenState={true}
            >
              <Stack gap={3} pb={3} pr={3} minHeight={350}>
                <Stack pt={3} gap={3}>
                  <ItemSelectInput
                    label="Issue"
                    placeholder="Select Issue"
                    items={ASSET_ISSUE_LIST}
                    item=""
                    value={form.issue.value}
                    error={fieldHasError("issue")}
                    handleChange={handleIssueSelect}
                    errorMessage={form.issue.errorMessage}
                    size="full"
                    required={true}
                  />

                  <Stack direction="row" gap={3}>
                    <ItemInput
                      label="Comment"
                      placeholder="Provide additional details about the asset condition or issue"
                      value={form.comments.value}
                      error={fieldHasError("comments")}
                      handleChange={updateInputField("comments")}
                      errorMessage={form.comments.errorMessage}
                      multiline
                      rows={5}
                      size="full"
                      required={true}
                    />
                    <ItemInput
                      label="Resolution"
                      placeholder="Enter how this issue will be resolved"
                      value={form.resolution.value}
                      error={fieldHasError("resolution")}
                      handleChange={updateInputField("resolution")}
                      errorMessage={form.resolution.errorMessage}
                      multiline
                      rows={5}
                      size="full"
                      required={true}
                    />
                  </Stack>

                  {existingImages.length > 0 && (
                    <Box>
                      <label
                        style={{
                          fontSize: 14,
                          marginBottom: 8,
                          display: "block",
                        }}
                      >
                        Existing Evidence Images
                      </label>
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {existingImages.map((img) => (
                          <Chip
                            key={img.id}
                            label={`Image ${img.id.slice(0, 8)}`}
                            onDelete={() => handleRemoveExistingImage(img.id)}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <FileInput
                    label="Add New Evidence Image(s)"
                    size="full"
                    multiple
                    onFileSelect={handleImageSelect}
                    resetFiles={resetFiles}
                    onResetComplete={handleResetComplete}
                    name="report-images"
                  />
                </Stack>
              </Stack>
            </ExpandableContainer>
          </Stack>

          <Divider variant="fullWidth" sx={{ my: 3 }} />

          <Stack width={"100%"}>
            <ExpandableContainer
              label="Assets Table"
              isTable={true}
              initOpenState
            >
              <VerticalGap height={20} />

              <AssetLogsFilter<IAssetRecord[]>
                ftn={assetApi.getAssets}
                updateDataSet={populateDataSet}
                updateQuerySet={setQuerySet}
              />

              <DamsTable
                tableTitle="Assets"
                tableSubtitle="Select assets to report"
                useTableCheckbox={true}
                selectedRowItems={selectedRowItems}
                data={dataset}
                tableHeaders={columns}
                tableKeys={tkeys}
                hiddenKeys={hkeys}
                hiddenLabels={hcolumns}
                hasPagination
                paginationState={pagination}
                maxRowCells={8}
                onTableItemSelect={toggleRow}
                toggleSelectAllRow={toggleSelectAllRow}
                onNextPage={paginateToNextPage(querySet, populateDataSet)}
                onPreviousPage={paginateToPreviousPage(
                  querySet,
                  populateDataSet,
                )}
                setActivePage={paginateToPageNumber(populateDataSet, querySet)}
                tableTitleBadge={
                  selectedRowItems.length === 0
                    ? undefined
                    : selectedRowItems.length + " selected"
                }
              />
              <VerticalGap height={30} />
            </ExpandableContainer>
          </Stack>
        </Box>
      </Box>
    </MainPageBase>
  );
};

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "date_received",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand (Model)",
  "Embossment Number",
  "Assignment Status",
  "Date Received",
];

const hkeys: (keyof IAssetRecord)[] = [
  "batch_number",
  "current_location",
  "current_location_domain",
  "description",
  "warranty_in_months",
  "asset_meta_raw",
];

const hcolumns = [
  "Batch Number",
  "Current Location",
  "current location domain",
  "Description",
  "Warranty (months)",
  "Additional Information",
];

export default UpdateAssetReportForm;
